import React from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiZap, FiPlay } = FiIcons;

const HeroSection = ({ featuredArtist }) => (
  <section className="relative min-h-screen flex items-end overflow-hidden page-transition">
    {/* Background image with parallax */}
    <div className="absolute inset-0">
      {featuredArtist?.cover_image ? (
        <img src={featuredArtist.cover_image} alt={featuredArtist.name}
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover grayscale opacity-30 mix-blend-luminosity scale-105" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
      )}
      
      {/* Enhanced gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/90 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/80 via-transparent to-zinc-950/80" />
      
      {/* Animated orbs */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-brand-pink/15 rounded-full blur-[200px] transform translate-x-1/3 -translate-y-1/3 pointer-events-none mix-blend-screen animate-orb-float" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-brand-coral/15 rounded-full blur-[150px] transform -translate-x-1/3 translate-y-1/3 pointer-events-none mix-blend-screen animate-orb-float" style={{ animationDelay: '3s' }} />
      
      {/* Subtle particle effect */}
      <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full blur-[1px] animate-pulse" />
      <div className="absolute top-1/3 right-1/4 w-1 h-1 bg-brand-pink/30 rounded-full blur-[1px] animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-brand-coral/25 rounded-full blur-[1px] animate-pulse" style={{ animationDelay: '2s' }} />
    </div>

    <div className="relative z-10 w-full max-w-7xl mx-auto px-8 pb-24 space-y-12">
      {/* Live badge */}
      <div className="flex items-center gap-3 animate-slide-up">
        <div className="relative">
          <div className="w-3 h-3 rounded-full bg-brand-coral animate-pulse" />
          <div className="absolute inset-0 w-3 h-3 rounded-full bg-brand-coral blur-md animate-pulse" />
        </div>
        <span className="text-xs font-black tracking-widest text-zinc-400 uppercase">Live Platform</span>
      </div>

      {/* Main hero content */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-12 animate-slide-up animation-delay-200">
        <div className="relative group">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-brand-pink to-brand-coral blur-2xl opacity-40 group-hover:opacity-60 transition-opacity duration-500" />
          <img
            src="https://media-manager-c.questera.ai/greta-media/5fb2f9d5255c375c4c593c40a3604cfd971c9f56c7725c3a05c28f383b843f2c3e4f4687d5d18e728c639d20e7900999/images/aW1hZ2UvcG5n/e291f38d345a3c31aa9db040447e5035.png"
            alt="Vision Direct Logo"
            width="224"
            height="224"
            fetchPriority="high"
            decoding="async"
            className="relative w-28 h-28 sm:w-36 sm:h-36 md:w-56 md:h-56 drop-shadow-[0_0_60px_rgba(255,102,204,0.8)] animate-float transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <h1 className="text-[clamp(3.5rem,12vw,10rem)] font-black tracking-tighter leading-[0.75] text-transparent bg-clip-text bg-gradient-to-br from-white via-brand-pink to-brand-coral uppercase italic animate-slide-up animation-delay-400 text-shadow-glow">
          Vision<br />Direct
        </h1>
      </div>

      {/* Description */}
      <p className="text-zinc-300 text-xl md:text-2xl font-bold max-w-2xl leading-relaxed animate-slide-up animation-delay-600">
        The future of creative independence. No labels. No middlemen. Pure music, direct connection.
      </p>

      {/* CTA buttons */}
      <div className="flex flex-wrap gap-4 pt-4 animate-scale-in animation-delay-600">
        <Link to="/auth"
          className="px-10 py-5 btn-brand rounded-full flex items-center gap-3 shadow-2xl text-sm group">
          <SafeIcon icon={FiZap} className="group-hover:rotate-12 transition-transform" /> Join Now
        </Link>
        {featuredArtist && (
          <Link to={`/${featuredArtist.slug}`}
            className="px-10 py-5 glass-card text-white font-black uppercase tracking-widest text-sm rounded-full hover:border-brand-pink transition-all flex items-center gap-3 group">
            Featured Artist <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-8 pt-12 border-t border-white/5 animate-fade-in animation-delay-600">
        {[
          { label: 'Active Artists', value: '500+' },
          { label: 'Exclusive Drops', value: '2.4K' },
          { label: 'Global Fans', value: '50K+' },
        ].map((stat, i) => (
          <div key={stat.label} className="space-y-1">
            <p className="text-3xl font-black text-white">{stat.value}</p>
            <p className="text-xs font-black uppercase tracking-widest text-zinc-500">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default HeroSection;